// Import polyfills first
import './polyfills';

import React from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { setThemeColors } from './utils/setThemeColors';
import { appConfig } from './config/appConfig';

// Set theme colors early based on environment variables
setThemeColors();

// Update document title based on app config (set immediately for browser tab)
if (typeof document !== 'undefined') {
  document.title = appConfig.appName;
  // Also update meta tags if needed
  const metaTitle = document.querySelector('meta[property="og:title"]');
  if (metaTitle) {
    metaTitle.setAttribute('content', appConfig.appName);
  }
}

// Suppress browser extension errors (harmless but noisy)
// These errors come from browser extensions, not the application
if (typeof window !== 'undefined') {
  // Suppress unhandled promise rejections from extensions
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || String(event.reason || '');
    
    // Filter out browser extension errors
    if (
      errorMessage.includes('message channel closed') ||
      errorMessage.includes('listener indicated an asynchronous response') ||
      errorMessage.includes('runtime.lastError') ||
      errorMessage.includes('Extension context invalidated')
    ) {
      // Suppress extension errors - they're harmless
      event.preventDefault();
      return;
    }
    
    // Log other unhandled rejections for debugging
    console.warn('Unhandled promise rejection:', event.reason);
  });

  // Suppress console errors from extensions
  const originalError = console.error;
  console.error = (...args: any[]) => {
    const errorMessage = args.join(' ');
    
    // Filter out browser extension errors
    if (
      errorMessage.includes('message channel closed') ||
      errorMessage.includes('listener indicated an asynchronous response') ||
      errorMessage.includes('runtime.lastError') ||
      errorMessage.includes('Extension context invalidated') ||
      errorMessage.includes('A listener indicated an asynchronous response')
    ) {
      // Suppress extension errors - don't log them
      return;
    }
    
    // Log other errors normally
    originalError.apply(console, args);
  };

  // Handle Chrome extension runtime errors
  if (typeof (window as any).chrome !== 'undefined' && (window as any).chrome.runtime) {
    try {
      // Check for lastError and clear it silently
      if ((window as any).chrome.runtime.lastError) {
        // Access it to clear it, but don't log
        const lastError = (window as any).chrome.runtime.lastError;
        if (lastError.message && (
          lastError.message.includes('message channel closed') ||
          lastError.message.includes('Extension context invalidated')
        )) {
          // Silently ignore extension errors
        }
      }
    } catch (e) {
      // Ignore errors when checking for extension errors
    }
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
